import * as React from 'react';

import { IOrganizationRoleModel, IUserModel } from '../../../interfaces/IUser';

import { CaseFileProvider } from '../../../contexts/CaseFileContext';
import { DatabaseAppSettingsEnum } from '../../../utilities/Constants';
import { DocumentTitle } from '../../shared/DocumentTitle';
import { GlobalAnnouncements } from '../../shared/announcements/GlobalAnnouncements';
import { HubConnection } from '@microsoft/signalr';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { MainContentDivProvider } from '../../../contexts/MainContentDivContext';
import { NavigationMenu } from './menus/NavigationMenu';
import SideNavMenu from './menus/SideNavMenu';
import { SignalRMethods } from '../../../utilities/SignalR';
import TagManager from 'react-gtm-module';
import { UserMenu } from './menus/UserMenu';
import { useEffect } from 'react';
import { useGetDbAppSettings } from '../../../shared/react-query-hooks/useGetDbAppSettings';
import { useGetUserUnreadQueryExports } from '../../../shared/react-query-hooks/useGetUserUnreadQueryExports';
import { useNavigate } from 'react-router-dom';
import { useZoneContext } from '../../../contexts/useZoneContext';

export interface ISecureLayoutProps {
    alertHubConnection: HubConnection | undefined;
    children: React.ReactNode;
    isLoggedIn: boolean;
    user: IUserModel;
}

export function SecureLayout(props: ISecureLayoutProps) {
    const appSettings = useGetDbAppSettings();
    const { zoneGuid, zoneChange } = useZoneContext();
    const navigate = useNavigate();

    const { refetch: refetchUnreadQueryExports } = useGetUserUnreadQueryExports(
        props.user ?? { active: false }
    );

    const handleZoneChange = async (org: IOrganizationRoleModel, redirectPath?: string) => {
        await zoneChange(org.guid!, org.name!, redirectPath);
    };

    const handleAppLogoClick = () => {
        navigate(LocalRoutes.QuadDashboard);
    };

    const reportSignalrState = () => {
        console.log(`SignalR - Alert Hub => State: ${props.alertHubConnection?.state}`);
    };

    const startConnection = async () => {
        try {
            if (props.alertHubConnection?.state === 'Disconnected') {
                await props.alertHubConnection?.start();
                reportSignalrState();
            }
        } catch (err) {
            console.log(err);
            reportSignalrState();

            window.setTimeout(() => startConnection(), 10000);
        }
    };

    const bindSignalrEvents = () => {
        const handleSendReportNotificationReceived = () => {
            // console.log(`******* ${SignalRMethods.SendReportNotification} *******`);
            refetchUnreadQueryExports();
        };

        if (props.alertHubConnection !== undefined) {
            console.log(
                `SignalR - Alert Hub => Listening for ${SignalRMethods.SendReportNotification}`
            );
            props.alertHubConnection?.on(
                SignalRMethods.SendReportNotification,
                handleSendReportNotificationReceived
            );
            props.alertHubConnection?.onclose(async () => {
                reportSignalrState();
                await startConnection();
            });
        }

        startConnection();
    };

    const unbindSignalrEvents = () => {
        console.log(
            `SignalR - Alert Hub => Stopped listening for ${SignalRMethods.SendReportNotification}`
        );
        props.alertHubConnection?.off(SignalRMethods.SendReportNotification);

        console.log(`SignalR - Alert Hub => Stopped`);
        props.alertHubConnection?.stop();
    };

    useEffect(() => {
        if (!appSettings.isLoading && appSettings.data) {
            const gtmId = appSettings.data.find(
                (setting) => setting.name === DatabaseAppSettingsEnum.GoogleTagManagerId
            )?.value;

            if (gtmId) {
                TagManager.initialize({
                    gtmId,
                });
            } else {
                console.log('Failed to load Google Tag Manager identifier.');
            }
        }
    }, [appSettings.isLoading, appSettings.data]);

    useEffect(() => {
        bindSignalrEvents();
        return unbindSignalrEvents;
    }, [props.alertHubConnection]);

    return (
        <>
            <DocumentTitle />
            <div className="app-container">
                <GlobalAnnouncements />
                <header className="top-nav print-hide-display">
                    <div className="logo-container">
                        <img
                            className="logo-img rounded"
                            src="/images/CheckmateLogo.svg"
                            onClick={handleAppLogoClick}
                            alt="Checkmate Logo"
                        />
                    </div>
                    <div className="nav-menu-item-container">
                        <NavigationMenu onZoneChange={handleZoneChange} />
                        <UserMenu onZoneChange={handleZoneChange} />
                    </div>
                </header>
                <div className="app-body">
                    <aside className="left-nav print-hide-display">
                        <SideNavMenu
                            isAuthenticated={props.isLoggedIn}
                            alertHubConnection={props.alertHubConnection}
                        />
                    </aside>
                    <CaseFileProvider>
                        <MainContentDivProvider>{props.children}</MainContentDivProvider>
                    </CaseFileProvider>
                </div>
            </div>
        </>
    );
}
